// src/pages/AIRecordings.jsx

import React, { useEffect, useState } from "react";
import API from "../api/api";
import { Bar } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function AIRecordings() {

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);

  const [sourcePhone, setSourcePhone] = useState("");
  const [destinationPhone, setDestinationPhone] = useState("");
  const [status, setStatus] = useState("");
  const [callTimestamp, setCallTimestamp] = useState("");

  const [showGraph, setShowGraph] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);

    try {

      const params = {
        page,
        limit
      };

      if (sourcePhone) params.source_phone = sourcePhone;
      if (destinationPhone) params.destination_phone = destinationPhone;
      if (status) params.status = status;
      if (callTimestamp) params.call_timestamp = callTimestamp;

      const res = await API.get("/ai-recordings", { params });

      setLogs(res.data.rows || []);
      setTotal(res.data.total || 0);

    } catch (err) {
      alert("Failed to fetch logs");
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page, limit]);

  const totalPages = Math.ceil(total / limit);

  const chartData = React.useMemo(() => {

    if (!logs.length) return null;

    const statusCounts = logs.reduce((acc, log) => {
      acc[log.status] = (acc[log.status] || 0) + 1;
      return acc;
    }, {});

    const labels = Object.keys(statusCounts);
    const data = labels.map((label) => statusCounts[label]);

    return {
      labels,
      datasets: [
        {
          label: "AI Calls",
          data,
          backgroundColor: "rgba(54,162,235,0.7)",
          borderRadius: 5,
        },
      ],
    };

  }, [logs]);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: { display: true, text: "Call Status Distribution" },
    },
  };

  const handleExportCSV = () => {

    if (!logs.length) return;

    const headers = [
      "ID",
      "Source Phone",
      "Destination Phone",
      "Status",
      "Call Timestamp",
      "Billable Seconds",
      "Recording URL",
    ];

    const rows = logs.map((log) => [
      log.id,
      log.source_phone,
      log.destination_phone,
      log.status,
      log.call_timestamp,
      log.billable_seconds,
      log.recording_url,
    ]);

    let csvContent = headers.join(",") + "\n";

    rows.forEach((row) => {
      csvContent += row.map((f) => `"${f}"`).join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", "AI_Recording_Logs.csv");

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  };

  return (

    <div className="container my-4">

      <h3 className="text-center text-primary fw-bold mb-4">
        AI Recording Logs
      </h3>

      {/* FILTER FORM */}

      <form
        className="row g-3 align-items-center mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          fetchLogs();
        }}
      >

        <div className="col-md-3">
          <input
            className="form-control"
            placeholder="Source Phone"
            value={sourcePhone}
            onChange={(e) => setSourcePhone(e.target.value)}
          />
        </div>

        <div className="col-md-3">
          <input
            className="form-control"
            placeholder="Destination Phone"
            value={destinationPhone}
            onChange={(e) => setDestinationPhone(e.target.value)}
          />
        </div>

        <div className="col-md-2">
          <select
            className="form-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="ANSWERED">Answered</option>
            <option value="NO ANSWER">No Answer</option>
            <option value="BUSY">Busy</option>
            <option value="FAILED">Failed</option>
          </select>
        </div>

        <div className="col-md-2">
          <input
            type="date"
            className="form-control"
            value={callTimestamp}
            onChange={(e) => setCallTimestamp(e.target.value)}
          />
        </div>

        <div className="col-md-1 d-grid">
          <button className="btn btn-primary">Filter</button>
        </div>

        <div className="col-md-1 d-grid">
          <button
            type="button"
            className="btn btn-success"
            onClick={handleExportCSV}
          >
            Export
          </button>
        </div>

      </form>

      {/* TABLE CONTROLS */}

      <div className="d-flex justify-content-between align-items-center mb-3">

        <div>

          <label className="me-2 fw-semibold">Show</label>

          <select
            className="form-select d-inline-block w-auto"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
          >

            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>

          </select>

          <span className="ms-2">entries</span>

        </div>

        <div>

          <button
            className="btn btn-outline-secondary"
            onClick={() => setShowGraph(!showGraph)}
          >
            {showGraph ? "Hide Chart" : "Show Chart"}
          </button>

        </div>

        <div className="text-muted small">
          Total Records : <b>{total}</b>
        </div>

      </div>

      {/* GRAPH */}

      {showGraph && chartData && (
        <div className="mb-4 p-3 border rounded shadow-sm bg-light">
          <Bar data={chartData} options={chartOptions} />
        </div>
      )}

      {/* TABLE */}

      {loading ? (

        <p className="text-center my-5">Loading...</p>

      ) : logs.length === 0 ? (

        <p className="text-center my-5 text-muted">No logs found</p>

      ) : (

        <div className="table-responsive shadow-sm rounded">

          <table className="table table-bordered table-striped mb-0">

            <thead className="table-dark">
              <tr>
                <th>ID</th>
                <th>Source</th>
                <th>Destination</th>
                <th>Status</th>
                <th>Call Time</th>
                <th>Seconds</th>
                <th>Recording</th>
              </tr>
            </thead>

            <tbody>

              {logs.map((log) => (

                <tr key={log.id}>

                  <td>{log.id}</td>

                  <td>{log.source_phone}</td>

                  <td>{log.destination_phone}</td>

                  <td>
                    <span className="badge bg-primary">
                      {log.status}
                    </span>
                  </td>

                  <td>
                    {new Date(log.call_timestamp).toLocaleString()}
                  </td>

                  <td>{log.billable_seconds}</td>

                  <td>

                    {log.recording_url ?

                      <a
                        href={log.recording_url}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-success"
                      >
                        Listen
                      </a>

                      :

                      "No File"

                    }

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      )}

      {/* PAGINATION */}

      {totalPages > 1 && (

        <div className="d-flex justify-content-center align-items-center mt-3 gap-3">

          <button
            className="btn btn-outline-primary"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </button>

          <span className="fw-semibold">
            Page {page} of {totalPages}
          </span>

          <button
            className="btn btn-outline-primary"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>

        </div>

      )}

    </div>

  );
}